<template>
  <Accordion
    :key="updateSpmAccordionKey"
    v-model:activeIndex="activePanel"
    multiple
    class="mb-2 spm-panel-accordion"
  >
    <AccordionTab
      v-for="panel in panels.filter((item) => !item.hide)"
      :key="panel.id"
    >
      <template #header>
        <span class="flex align-items-center gap-2 w-full">
          {{ t(panel.title) }}
        </span>
      </template>
      <slot :name="panel.id" />
    </AccordionTab>
  </Accordion>
</template>

<script lang="ts">
import {
  defineComponent, onBeforeMount,
  PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';

export interface SpmPanelAccordionInterface {
  id: string;
  title: string;
  hide?: boolean;
}

export default defineComponent({
  name: 'PanelAccordion',

  components: {
    Accordion,
    AccordionTab,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    // Array of panels to display
    panels: {
      type: Array as PropType<SpmPanelAccordionInterface[]>,
      required: true,
    },

    // If true, always keep one panel open
    alwaysKeepOneOpen: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },

  setup(props: { panels: object[]; alwaysKeepOneOpen: boolean }) {
    const { t } = useI18n();
    const activePanel = ref<number[]>([0]);
    const updateSpmAccordionKey = ref(0);

    // If alwaysKeepOneOpen is true, watch activePanel and prevent it from being null
    if (props.alwaysKeepOneOpen) {
      watch(activePanel, (newValue: number[], oldValue: number[]) => {
        if (newValue.length === 0) {
          activePanel.value = oldValue.length === 0 ? [0] : oldValue;
          updateSpmAccordionKey.value += 1;
        }
      });
    }

    onBeforeMount(() => {
      // all panels are open by default
      activePanel.value = props.panels.map((_, index) => index);
    });

    return {
      activePanel,
      updateSpmAccordionKey,
      t,
    };
  },
});
</script>

<style lang="scss">
.spm-panel-accordion {
  margin-bottom: 1rem !important;

  .p-accordion-tab {
    margin-bottom: 0.5rem !important;
    border: none !important;
    box-shadow: none !important; // Remove default shadows

    .p-accordion-header {
      .p-accordion-header-link {
        background: #f8f9fa; // Light gray active/inactive base
        border: 1px solid #e9ecef; // Subtle border
        border-radius: 6px;
        color: $tuna;
        font-weight: 600; // Popped text
        padding: 0.75rem 1rem;
        transition: all 0.2s ease;
        text-decoration: none !important;
        box-shadow: none !important;

        .p-accordion-toggle-icon {
          color: $heather; // Subtler icon
        }

        &:hover {
           background: #e9ecef; // Slight darken on hover
        }
      }

      &.p-highlight .p-accordion-header-link {
        border-bottom-left-radius: 0; // Seamless connect to content
        border-bottom-right-radius: 0;
        background: #fff; // Active header white to match content area usually
        border-bottom-color: transparent; // Open look
        
        .p-accordion-toggle-icon {
          color: $brand-color-primary; // Active icon color
        }
      }
    }

    .p-accordion-content {
      border: 1px solid #e9ecef;
      border-top: none; // Connect to header
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
      padding: 1rem !important;
      background: #fff;
    }
  }
}
</style>
